"use client";

import React, { useState } from 'react';

interface Partner {
  id: string | number;
  nom: string;
  categorie: string;
  description: string;
  siteWeb: string;
}

interface Props {
  categories: string[];
  partners: Partner[];
  categorieColors: Record<string, { color: string; bg: string }>;
}

export default function PartenairesFilter({ categories, partners, categorieColors }: Props) {
  const [activeCategory, setActiveCategory] = useState('Tous');

  const filtered = activeCategory === 'Tous'
    ? partners
    : partners.filter((p) => p.categorie === activeCategory);

  return (
    <>
      {/* Category filter */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((cat) =>
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${cat === activeCategory ? 'bg-primary text-white' : 'bg-gray-100 text-text-muted hover:bg-primary/10 hover:text-primary'}`}
                style={{ fontFamily: 'Poppins, sans-serif' }}>
                {cat}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>Aucun partenaire dans cette catégorie.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((partner) => {
              const colours = categorieColors[partner.categorie] ?? { color: 'text-gray-700', bg: 'bg-gray-100' };
              return (
                <div key={partner.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col">
                  {/* Logo area */}
                  <div className="h-40 bg-gray-50 flex items-center justify-center p-6 border-b border-gray-100">
                    <div className="w-20 h-20 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#DB052C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <span className={`inline-flex self-start items-center px-3 py-1 rounded-full text-xs font-semibold mb-3 ${colours.bg} ${colours.color}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {partner.categorie}
                    </span>
                    <h3 className="font-bold text-text-main text-base mb-2 leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>{partner.nom}</h3>
                    <p className="text-text-muted text-sm leading-relaxed flex-1 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>{partner.description}</p>
                    {partner.siteWeb && (
                      <a
                        href={partner.siteWeb}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold hover:underline"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        aria-label={`Visiter le site de ${partner.nom} (ouvre dans un nouvel onglet)`}>
                        Visiter le site
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
